import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cachedGeo, getGeo } from '../lib/personal';

/**
 * Interface translation. English strings are the keys, so a string with no
 * translation simply shows in English, and migrating a component is
 * wrapping its literals in t(). Interpolation: t('{n} upcoming events',
 * { n }); dictionaries may give a singular|plural pair split on '|'.
 *
 * What is translated: the chrome — navigation, controls, section headings,
 * labels, notes, buttons. Event names, descriptions, blog posts and the
 * legal pages stay English (phase 2 is translated content on /de/… URLs).
 *
 * Prerendered HTML is English; a visitor's saved or country-chosen language
 * is applied after mount, so a German visitor sees one English frame first.
 * Hydration mismatches that causes are the recoverable kind React re-renders
 * through (see main.tsx).
 *
 * The four dictionaries are chunks of their own, fetched when a visitor's language needs one
 * (loadLang): in the main bundle they were 228 KB of its 780 KB, downloaded and parsed on every
 * phone while nearly every visitor reads English (2026-10-19). The switch waits for the chunk:
 * main.tsx starts the one a visit will need before the app hydrates (preloadLang), the menu starts
 * all four when it opens, and a country default changes the currency in the same render
 * (pageLangFor). A chunk that fails cannot be fetched again by the same page (loadLang).
 */
export type Lang = 'en' | 'de' | 'es' | 'fr' | 'nl';

export const LANGS: { code: Lang; name: string; english: string }[] = [
  { code: 'en', name: 'English', english: 'English' },
  { code: 'de', name: 'Deutsch', english: 'German' },
  { code: 'es', name: 'Español', english: 'Spanish' },
  { code: 'fr', name: 'Français', english: 'French' },
  { code: 'nl', name: 'Nederlands', english: 'Dutch' },
];

/** First-visit default by country (the visitor can always switch). Belgium defaults to
 *  Dutch, its majority language; in the countries of SPOKEN the browser's own language
 *  decides first (a French-speaking Belgian or Swiss visitor gets French). */
export const LANG_BY_COUNTRY: Record<string, Lang> = {
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es',
  DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  FR: 'fr', LU: 'fr', MC: 'fr',
  NL: 'nl', BE: 'nl',
};

/** Countries where more than one of our languages is spoken. */
const SPOKEN: Record<string, Lang[]> = { BE: ['nl', 'fr', 'de'], CH: ['de', 'fr'], LU: ['fr', 'de'], CA: ['en', 'fr'] };

const browserLangs = (): string[] => {
  try {
    const list = navigator.languages?.length ? navigator.languages : [navigator.language];
    return list.filter(Boolean).map((l) => l.slice(0, 2).toLowerCase());
  } catch {
    return [];
  }
};

/** The first-visit language for a visitor's country: in a multilingual country the
 *  browser's preferred language among those spoken there, else the country default. */
export const langForCountry = (country: string): Lang | undefined => {
  const spoken = SPOKEN[country];
  const pick = spoken ? browserLangs().find((l) => (spoken as string[]).includes(l)) : undefined;
  return isLang(pick) ? pick : LANG_BY_COUNTRY[country];
};

type Dict = Record<string, string>;
const DICTS: Partial<Record<Lang, Dict>> = { en: {} };
const LOADERS: Record<Exclude<Lang, 'en'>, () => Promise<Dict>> = {
  de: () => import('./de').then((m) => m.de),
  es: () => import('./es').then((m) => m.es),
  fr: () => import('./fr').then((m) => m.fr),
  nl: () => import('./nl').then((m) => m.nl),
};
const loading = new Map<Lang, Promise<boolean>>();

/**
 * A language's dictionary, fetched once: true when it is ready, false when the chunk could not
 * be loaded (the page then stays in English, as for a key with no translation).
 *
 * A chunk that failed once cannot be fetched again BY THIS DOCUMENT: every engine keeps a failed
 * module fetch in the document's module map for the document's life (Chromium, WebKit and Firefox
 * alike, review 2026-10-19; whatwg/html#6768), so a second import() of the same URL rejects at once
 * without a request, and the source cannot ask for another URL (Vite names the built chunk). The
 * failed entry is still dropped below, so an engine that retries failed module fetches gets a real
 * second try; today only a new document gets one (LanguageProvider's setLang reloads once).
 */
export const loadLang = (l: Lang): Promise<boolean> => {
  if (DICTS[l]) return Promise.resolve(true);
  let p = loading.get(l);
  if (!p) {
    p = LOADERS[l as Exclude<Lang, 'en'>]()
      .then((d) => {
        DICTS[l] = d;
        return true;
      })
      .catch(() => {
        loading.delete(l);
        return false;
      });
    loading.set(l, p);
  }
  return p;
};

const KEY = 'tixinfo.lang';
const CODES = LANGS.map((l) => l.code) as string[];
const isLang = (v: unknown): v is Lang => typeof v === 'string' && CODES.includes(v);

const savedLang = (): Lang | null => {
  try {
    const saved = localStorage.getItem(KEY);
    return isLang(saved) ? saved : null;
  } catch {
    return null;
  }
};

/** The language this visitor's page settles on once the provider knows `country`: the saved one,
 *  else the country's default; null for English (nothing to download). src/lib/currency.ts waits
 *  for its dictionary, so the currency, the labels and the note under the header change in one
 *  render, as when the dictionaries were in the bundle. */
export const pageLangFor = (country: string | null | undefined): Lang | null => {
  const l = savedLang() ?? (country ? langForCountry(country) : undefined);
  return l && l !== 'en' ? l : null;
};

/** A first visit's likely language before /api/geo answers: the default of the region in the
 *  browser's first language tag ("de-AT" → Austria's, "es-US" → none), else that language itself
 *  when it is one of ours ("de"). */
const browserGuess = (): Lang | undefined => {
  try {
    const [base = '', ...rest] = (navigator.languages?.[0] || navigator.language || '').split('-');
    const region = rest.find((p) => /^[a-z]{2}$/i.test(p));
    if (region) return langForCountry(region.toUpperCase());
    const l = base.toLowerCase();
    return isLang(l) ? l : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Browser boot (main.tsx, before the app hydrates): start downloading the dictionary this visit
 * will need, so it is here when the provider switches — the saved language; else the default of
 * the country this session's /api/geo answered (sessionStorage); on a first visit, the browser's
 * guess. An English visitor downloads nothing; a wrong guess costs one ~15 KB chunk (the provider
 * still decides from /api/geo). The first render stays English either way, so hydration matches.
 */
export const preloadLang = () => {
  const geo = cachedGeo();
  const l = savedLang() ?? (geo ? (geo.country ? langForCountry(geo.country) : undefined) : browserGuess());
  if (l && l !== 'en') void loadLang(l);
};

type Vars = Record<string, string | number>;

const fill = (s: string, vars?: Vars) =>
  vars ? s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : s;

/** Translate one string. A dictionary entry "one|many" picks by vars.n. */
export const translate = (lang: Lang, key: string, vars?: Vars) => {
  let s = DICTS[lang]?.[key] ?? key;
  if (s.includes('|')) {
    const [one, many] = s.split('|');
    s = vars && Number(vars.n) === 1 ? one : many;
  } else if (key.includes('|')) {
    const [one, many] = key.split('|');
    s = vars && Number(vars.n) === 1 ? one : many;
  }
  return fill(s, vars);
};

interface Ctx {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** set when the language was chosen from the visitor's country, until dismissed */
  auto: Lang | null;
  dismissAuto: () => void;
  t: (key: string, vars?: Vars) => string;
}

const LanguageContext = createContext<Ctx>({
  lang: 'en',
  setLang: () => {},
  auto: null,
  dismissAuto: () => {},
  t: (key, vars) => translate('en', key, vars),
});

const NOTED_KEY = 'tixinfo.lang.noted';
const RELOAD_KEY = 'tixinfo.lang.reload';

/**
 * A language picked in the menu whose dictionary did not load (a dropped request on a phone, a
 * chunk an old tab can no longer find): this document can never fetch that chunk again (loadLang),
 * a new one can, so reload once — the choice is saved first, and main.tsx starts its chunk before
 * the new page hydrates. Never offline (the reload would land on the browser's error page), never
 * when the choice could not be saved, and at most once in 30 s per tab, so a chunk that keeps
 * failing cannot loop. A saved language or a country default that fails at boot stays in English
 * and never reloads.
 */
const reloadForLang = (l: Lang) => {
  try {
    if (navigator.onLine === false || localStorage.getItem(KEY) !== l) return;
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Math.abs(Date.now() - last) < 30_000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  location.reload();
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setState] = useState<Lang>('en');
  const [auto, setAuto] = useState<Lang | null>(null);
  // The language last asked for: a dictionary that arrives after a newer choice is not applied.
  const wanted = useRef<Lang>('en');
  // The visitor picked one in the menu: the country's default no longer applies.
  const picked = useRef(false);
  // The note under the header has nothing to announce: the visitor saw it before, closed it or
  // picked a language. Read again when a country default lands after its dictionary download, so a
  // close or a pick made during the download is not undone by the note coming back.
  const quiet = useRef(false);
  /** Switch once the dictionary is here; `then` runs after the switch, `failed` when its chunk
   *  could not be loaded (and no newer choice was made meanwhile). */
  const switchTo = useCallback((l: Lang, then?: () => void, failed?: () => void) => {
    wanted.current = l;
    if (DICTS[l]) {
      setState(l);
      then?.();
      return;
    }
    void loadLang(l).then((ok) => {
      if (wanted.current !== l) return;
      if (!ok) {
        failed?.();
        return;
      }
      setState(l);
      then?.();
    });
  }, []);

  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(KEY);
      if (localStorage.getItem(NOTED_KEY) === '1') quiet.current = true;
    } catch {
      /* no storage */
    }
    if (isLang(saved)) {
      switchTo(saved);
      return;
    }
    let alive = true;
    getGeo().then((g) => {
      const l = g?.country ? langForCountry(g.country) : undefined;
      // English is the page as served: nothing to switch, nothing to announce.
      if (!alive || !l || l === 'en') return;
      // a choice made meanwhile (the menu) wins over the country's default
      if (picked.current) return;
      switchTo(l, () => {
        if (!quiet.current) setAuto(l);
      });
    });
    return () => {
      alive = false;
    };
  }, [switchTo]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    picked.current = true;
    quiet.current = true;
    // saved before the switch: a pick whose chunk fails reloads into it (reloadForLang)
    try {
      localStorage.setItem(KEY, l);
      localStorage.setItem(NOTED_KEY, '1');
    } catch {
      /* private mode */
    }
    switchTo(l, undefined, () => reloadForLang(l));
    setAuto(null);
  }, [switchTo]);

  const dismissAuto = useCallback(() => {
    quiet.current = true;
    setAuto(null);
    try {
      localStorage.setItem(NOTED_KEY, '1');
    } catch {
      /* no storage */
    }
  }, []);

  const t = useCallback((key: string, vars?: Vars) => translate(lang, key, vars), [lang]);
  const value = useMemo(() => ({ lang, setLang, auto, dismissAuto, t }), [lang, setLang, auto, dismissAuto, t]);
  return createElement(LanguageContext.Provider, { value }, children);
};

export const useLanguage = () => useContext(LanguageContext);
export const useT = () => useContext(LanguageContext).t;
/** Intl locale of the current language, for dates and numbers. */
export const useLocale = () => localeOf(useContext(LanguageContext).lang);

/** Intl locale for dates and numbers in the current language. */
export const localeOf = (lang: Lang) =>
  ({ en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', nl: 'nl-NL' })[lang];
